import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenderService, 
  type GetAllTendersWithPaginationRequest, 
  type PaginatedTendersResponse, 
  type GetTenderByIdOrSlugRequest, 
  type GetTenderByIdOrSlugResponse 
} from '../services/tenderService';

export { tenderService };

// Get all tenders with pagination
export function useGetAllTendersWithPagination(
  params: GetAllTendersWithPaginationRequest = {},
  enabled = true
) {
  return useQuery({
    queryKey: ['tenders', 'paginated', params],
    queryFn: async () => {
      const res = await tenderService.getAllTendersWithPagination(params);
      return res.responseValue as PaginatedTendersResponse;
    },
    enabled,
  });
}

// Get tender by ID
export function useGetTenderById(id: number, version?: number, enabled = true) {
  return useQuery({
    queryKey: ['tenders', 'by-id', id, version],
    queryFn: async () => {
      const params: { Id: number; Version?: number } = { Id: id };
      if (version !== undefined) {
        params.Version = version;
      }
      const res = await tenderService.getTenderByIdOrSlug(params);
      return res.responseValue as GetTenderByIdOrSlugResponse;
    },
    enabled: enabled && !!id,
  });
}

// Get tender by slug
export function useGetTenderBySlug(slug: string, version?: number, enabled = true) {
  return useQuery({
    queryKey: ['tenders', 'by-slug', slug, version],
    queryFn: async () => {
      const params: { Slug: string; Version?: number } = { Slug: slug };
      if (version !== undefined) {
        params.Version = version;
      }
      const res = await tenderService.getTenderByIdOrSlug(params);
      return res.responseValue as GetTenderByIdOrSlugResponse;
    },
    enabled: enabled && !!slug,
  });
}

// Get tender by ID or slug (flexible)
export function useGetTenderByIdOrSlug(data: GetTenderByIdOrSlugRequest, enabled = true) {
  return useQuery({
    queryKey: ['tenders', 'by-id-or-slug', data.Id, data.Slug, data.Version],
    queryFn: async () => {
      const res = await tenderService.getTenderByIdOrSlug(data);
      return res.responseValue as GetTenderByIdOrSlugResponse;
    },
    enabled: enabled && (!!data.Id || !!data.Slug),
  });
}

// Refresh tenders cache
export function useRefreshTenders() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: ['tenders'] });
  };
}
